<template>
  <templates-contentful-content
    v-if="post"
    v-bind="propsData"
    template="ArticleWithSidebarV2"
    :llm-type="post?.vertical"
    :data-testid="dataTestid"
  >
    <template #main-content>
      <p v-if="post.timeToRead">
        <strong>Time to read : {{ post.timeToRead }} Minutes</strong>
      </p>

      <RichTextRenderer
        :document="post.mainContent.json"
        :node-renderers="renderNodes()"
        :mark-renderers="RENDER_MARKS"
      />

      <hr class="my-5 border-b border-b-gray-400" />

      <ab-test-wrapper
        v-if="_slug === 'have-you-become-a-mortgage-prisoner'"
        css-class="ab-test-refinance-calculator show-a mb-6"
      >
        <template #component_b>
          <MoleculesCalculatorWithDisclaimer calculator="refinance" />
        </template>
      </ab-test-wrapper>

      <MoleculesShareButtons
        class="mb-9"
        title="Share this article"
        :buttons="[
          {
            name: 'Facebook',
            url: `https://www.facebook.com/sharer/sharer.php?u=${articleLink}`,
          },
          {
            name: 'Twitter',
            url: `https://twitter.com/share?text=${title}&url=${articleLink}`
          },
          {
            name: 'Linkedin',
            url: `https://www.linkedin.com/shareArticle?mini=true&url=${articleLink}`,
          },
          {
            name: 'Whatsapp',
            url: `https://api.whatsapp.com/send?text=${articleLink}`,
          },
          {
            name: 'Copy',
            url: articleLink,
          },
        ]"
      />
    </template>

    <template #sidebar-right>
      <V2MoleculesPostTileGroup
        :items="latestNews"
        title-class="mb-6"
        title="Latest articles"
        type="sidebar"
        layout="small-tile"
        has-divider
        align-center
      />
    </template>

    <template #footer>
      <div class="bg-gray-10 px-4 xl:px-0 pt-6 pb-[2.625rem] md:pt-10 md:pb-12">
        <div class="w-full max-w-wrapper-lg mx-auto">
          <V2AtomsHighlight
            class="font-semibold !text-semantic-sizing-headline-sm mb-8"
            size="base"
          >
            About the author
          </V2AtomsHighlight>

          <V2OrganismAuthorWidget
            class="max-w-wrapper-sm mb-8"
            :author-name="post.author?.name"
            :author-image="post.author?.image"
            :author-url="post.author?.url"
            :job-title="post.author?.jobTitle"
            :social-media="post.author?.socialMedia"
          >
            <p>
              {{ post.author?.profileShort ?? post.author?.profile }}
            </p>
          </V2OrganismAuthorWidget>

          <hr class="mb-8 border-b border-b-semantic-outline-variant" />

          <template v-if="!_isEmpty(authorPosts)">
            <V2MoleculesPostTileGroup
              :items="authorPosts?.items"
              :items-per-row="3"
              badge-theme="default"
              image-lazy-load
            />
          </template>
        </div>
      </div>

      <div class="pt-10 px-4 xl:px-0 pb-[2.625rem] md:pb-12">
        <V2MoleculesPostTileGroup
          class="w-full max-w-wrapper-lg mx-auto"
          title="Related News"
          title-size="large"
          title-class="mb-8 !text-semantic-sizing-headline-sm"
          badge-theme="default"
          :items="relatedPosts?.items"
          :items-per-row="3"
          :has-load-more="relatedPosts.items.length !== relatedPosts.total"
          image-lazy-load
          @load-more="handleLoadMore"
        />
      </div>
    </template>
  </templates-contentful-content>
</template>

<script setup>
import _isEmpty from 'underscore/cjs/isEmpty.js'

import dayjs from 'dayjs'
import RichTextRenderer from 'contentful-rich-text-vue-renderer'

import {
  RENDER_NODES,
  RENDER_MARKS,
  GET_FEATURE_IMAGE
} from '~/helpers/contentful'

import {
  DEFAULT_AUTHOR_URL,
  EXPERT_EASE_LOAD_MORE_PER_PAGE
} from '~/constants/pages/contentful'

defineOptions({
  name: 'TemplatesContentfulExpertAnalysis'
})

const props = defineProps({
  dataTestid: {
    type: String,
    default: 'contentful-expert-analysis'
  },

  isPreview: {
    type: Boolean,
    default: false
  }
})

const {
  internalDomainForInlinesHyperlink
} = useEnv()

const expertAnalysisStore = useExpertAnalysisStore()

const { $bus } = useNuxtApp()
const { devices } = deviceChecker()
const $route = useRoute()

const _category = $route.params.category
const _slug = $route.params.slug

const path = `/expert-analysis/${_category}/${_slug}`
const articleLink = import.meta.client ? window.location.href : ''

const { post, latestNews } = await useAsyncData(`ea-post-${_slug}`, () => expertAnalysisStore.getPost({
  slug: path,
  preview: props.isPreview
})).then(res => res?.data.value)

if (_isEmpty(post)) {
  throw createError({ statusCode: 404, statusMessage: 'Not Found' })
}

const title = (() => {
  if (
    post?.titleRichText &&
    typeof post?.titleRichText !== 'undefined'
  ) {
    const parse = documentToHtmlString(post?.titleRichText?.json)

    if (removeHtmlString(parse) !== '') {
      return parse.replace(/&amp;/g, '&')
    }
  }

  return post.title
})()

const authorUrl = computed(() => `${DEFAULT_AUTHOR_URL}${post?.author?.url}`)

const tags = computed(() => {
  const temp = post?.contentfulMetadata?.tags

  return !_isEmpty(temp)
    ? temp.map(tag => tag.id)
    : temp
})

const propsData = computed(() => {
  return {
    title: post.title,
    lastUpdate: post?.date ?? post.sys?.publishedAt,
    featuredImage: GET_FEATURE_IMAGE(
      post.featureImage,
      'ArticleWithSidebarV2',
      devices.currBreakpoint.value
    ),
    hasShareButtons: true,
    postType: 'Expert Analysis',
    author: post.author,
    editor: post.editor,
    tags: post?.contentfulMetadata?.tags,
    breadcrumbs: [
      {
        text: 'Home',
        name: 'Home',
        url: '/'
      },
      {
        text: 'Expert Analysis',
        name: 'Everyday ExpertEase',
        url: '/expert-analysis/'
      },
      {
        text: post.category,
        name: post.category,
        url: `/expert-analysis/${_category}`
      },
      {
        text: removeHtmlString(title),
        url: `${post.slug}/`,
        name: removeHtmlString(title),
        active: true
      }
    ]
  }
})

const relatedPostsPageConfig = ref()

const { data: relatedData } = await useAsyncData(`ea-related-${path}`, async () => {
  const [
    relatedPostsData,
    authorPostsData
  ] = await Promise.all([
    expertAnalysisStore.getRelatedPosts({
      tags: tags.value,
      slug: path,
      category: post.category,
      limit: EXPERT_EASE_LOAD_MORE_PER_PAGE
    }),
    expertAnalysisStore.getPostsByAuthor({
      author: post.author.name,
      slug: path,
      limit: EXPERT_EASE_LOAD_MORE_PER_PAGE
    })
  ])

  return {
    relatedPostsData,
    authorPostsData
  }
})

relatedPostsPageConfig.value = {
  limit: 3,
  total: relatedData.value?.relatedPostsData?.total
}

const relatedPosts = reactive(relatedData.value?.relatedPostsData ?? { items: [], total: 0 })
const authorPosts = relatedData.value?.authorPostsData

const noCanonicalLink = !!post?.noCanonicalLink
const noIndex = props.isPreview
  ? false
  : !!post?.noIndex
const canonicalLink = trailingSlash(post?.canonicalLink || `${CC_DOMAIN}${post?.slug}/`)

const entriesMap = (() => {
  const map = new Map()

  if (_isEmpty(post)) {
    return map
  }

  const links = post.mainContent?.links

  for (const entry of links.entries.block) {
    map.set(entry.sys.id, entry)
  }

  return map
})()

const assetsMap = (() => {
  const map = new Map()

  if (_isEmpty(post)) {
    return map
  }

  const links = post.mainContent?.links

  for (const assets of links.assets.block) {
    map.set(assets.sys.id, assets)
  }

  return map
})()

const renderNodes = () => RENDER_NODES({
  entriesMap,
  assetsMap,
  internalDomainForInlinesHyperlink,
  author: post?.author,
  inlineComponents: {
    CTA: resolveComponent('AtomsCta'),
    MoleculesCalculatorWithDisclaimer: resolveComponent('MoleculesCalculatorWithDisclaimer'),
    KeyPoints: resolveComponent('MoleculesKeyPoints'),
    InlineAge: resolveComponent('MoleculesInlineAge'),
    InlineCardCta: resolveComponent('V2MoleculesInlineCardCta'),
    InlineLifeStage: resolveComponent('MoleculesInlineLifeStage'),
    InlinePartner: resolveComponent('MoleculesInlinePartner'),
    VerticalFormWidget: resolveComponent('V2MoleculesVerticalFormWidgetSidebar'),

    MoleculesInlineLifeStage: resolveComponent('MoleculesInlineLifeStage'),
    Lifestage: resolveComponent('OrganismsInlineLifeStageAbTest'),
    State: resolveComponent('V2MoleculesInlineCardCta'),
    InlineAgeAbTest: resolveComponent('OrganismsInlineAgeAbTest'),
    AgeRange: resolveComponent('V2MoleculesInlineCardCta'),
    Partner: resolveComponent('MoleculesInlinePartner'),

    AUTHORBLOCK: resolveComponent('AtomsAuthorBlock'),
    FaqItem: resolveComponent('AtomsAccordion'),

    VideoAsset: resolveComponent('AtomsVideoFrame')
  }
})

async function handleLoadMore () {
  const offset = relatedPostsPageConfig.value?.limit
  const total = relatedPostsPageConfig.value?.total

  await expertAnalysisStore.getRelatedPosts({
    tags: tags.value,
    slug: path,
    category: post?.category,
    limit: EXPERT_EASE_LOAD_MORE_PER_PAGE,
    offset
  }).then(res => {
    const data = JSON.parse(JSON.stringify(res))
    const items = data?.items

    if (!_isEmpty(items)) {
      if (isObject(items)) {
        relatedPosts.items.push(items)
      } else {
        relatedPosts.items.push(...items)
      }
    }

    const newOffset = offset + EXPERT_EASE_LOAD_MORE_PER_PAGE

    relatedPostsPageConfig.value.limit = newOffset >= total
      ? total
      : newOffset

    return res
  }).catch(error => {
    throw new Error(error)
  })
}

const postDate = post?.sys?.firstPublishedAt ?? ''

$bus.emit('pageview', {
  pageCategory: post?.category ?? 'category',
  pageVertical: post?.vertical ?? 'vertical',
  pagePostAuthor: post?.author?.name ?? 'Compare Club',
  pagePostDateIso: postDate,
  pagePostDate: postDate
    ? dayjs(postDate).format('DD MMMM YYYY')
    : '',
  pagePostDateYear: postDate
    ? dayjs(postDate).format('YYYY')
    : '',
  pagePostDateMonth: postDate
    ? dayjs(postDate).format('MM')
    : '',
  pagePostDateDay: postDate
    ? dayjs(postDate).format('DD')
    : '',
  pagePostDateDayName: postDate
    ? dayjs(postDate).format('dddd')
    : '',
  pagePostDateHour: postDate
    ? dayjs(postDate).format('HH')
    : '',
  pagePostDateMinute: postDate
    ? dayjs(postDate).format('MM')
    : ''
})

const PAGE_DETAILS = {
  title: removeHtmlString(title) || 'Compare Club',
  ogTitle: post?.seoTitle ?? removeHtmlString(title) ?? 'Compare Club',
  description: post?.metaDescription ?? '',
  ogUrl: canonicalLink,
  ogImage: propsData.value.featuredImage?.url || FEATURED_IMAGE_DEFAULT_IMAGE,
  ogType: 'product'
}

generateMeta({
  title: PAGE_DETAILS.title,
  ogTitle: PAGE_DETAILS.ogTitle,

  description: PAGE_DETAILS.description,
  ogDescription: PAGE_DETAILS.description,

  canonical: noCanonicalLink
    ? ''
    : PAGE_DETAILS.ogUrl,
  ogUrl: canonicalLink,
  ogImage: PAGE_DETAILS.ogImage,
  ogType: PAGE_DETAILS.ogType,
  meta: [
    {
      itemprop: 'mainEntityOfPage',
      content: canonicalLink
    },
    {
      itemprop: 'dateModified',
      content: post.sys.publishedAt
    }
  ],
  noIndex,
  noFollow: noIndex
})

const faqList = (() => {
  const list = !_isEmpty(post?.faqSchema) && !post.hideFaqSchema
    ? post?.faqSchema
    : undefined

  if (!list) {
    return []
  }

  return list?.map(item => ({
    title: item?.key ?? '',
    content: item?.value ?? ''
  }))
})()

useJsonld(() => {
  if (props.isPreview) {
    return null
  }

  const _path = trailingSlash($route.path)

  const routePage = 'expert-analysis'
  const routeSlug = $route.params?.slug

  const datePublished = postDate
  const dateModified = post.sys.publishedAt

  const authorName = post?.author?.name

  return {
    '@context': 'https://schema.org',
    '@graph': [
      GENERATE_LOGO_SCHEMA({
        articleUrl: `${trailingSlash(`${CC_DOMAIN}${_path}`)}#logo`,
        imageUrl: post?.featureImage?.url || FEATURED_IMAGE_DEFAULT_IMAGE,
        caption: post?.featureImage?.description || 'Compare Club'
      }),
      GENERATE_WEBPAGE_SCHEMA({
        page: _path,
        pageTitle: PAGE_DETAILS?.ogTitle ?? PAGE_DETAILS.title,
        pageDescription: PAGE_DETAILS.description,
        aboutPath: `/${routePage}/#service`,
        mainEntityPath: `${_path}#article`,
        primaryImageOfPagePath: `${_path}#primaryimage`,
        breadcrumbPath: `${_path}#breadcrumb`,
        datePublished,
        dateModified
      }),
      GENERATE_ARTICLE_SCHEMA({
        page: routePage,
        slug: routeSlug,
        author: authorName,
        headline: PAGE_DETAILS.title,
        description: PAGE_DETAILS.description,
        audienceName: post?.audienceName ?? `Australian considering ${post?.vertical.replace(/-/g, ' ')}`,
        datePublished,
        dateModified
      }),
      GENERATE_PERSON_SCHEMA({
        page: authorUrl.value,
        author: authorName
      }),
      GENERATE_FAQ_SCHEMA({
        page: _path,
        faqs: faqList
      })
    ].filter(item => item)
  }
})
</script>
